import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import {
  GroupAdd as GroupAddIcon,
  NotificationsActive as NotifyIcon,
} from '@mui/icons-material';
import { Client, Sector, Company } from '../types/client';
import { ClientService } from '../services/clientService';

interface BulkActionsProps {
  clients: Client[];
  sectors: Sector[];
  companies: Company[];
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

const BulkActions: React.FC<BulkActionsProps> = ({
  clients,
  sectors,
  companies,
  onSuccess,
  onError,
}) => {
  // Batch Interest Assignment
  const [batchSector, setBatchSector] = useState<number | ''>('');
  const [batchCompany, setBatchCompany] = useState<number | ''>('');
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [assignDialog, setAssignDialog] = useState(false);

  // Notification Control
  const [notifySector, setNotifySector] = useState('');
  const [notifyEnabled, setNotifyEnabled] = useState(true);
  const [notifyDialog, setNotifyDialog] = useState(false);

  const [processing, setProcessing] = useState(false);

  // ── Batch Interest Assignment ───────────────────────────────────────

  const handleBatchAssign = async () => {
    if (selectedClientIds.length === 0) {
      onError('Select at least one client');
      return;
    }
    if (!batchSector && !batchCompany) {
      onError('Select a sector or company to assign');
      return;
    }

    setProcessing(true);
    try {
      const interest: { sector_id?: number; company_id?: number; sector_name?: string } = {};
      if (batchSector) {
        interest.sector_id = batchSector as number;
        const sec = sectors.find(s => s.id === batchSector);
        if (sec) interest.sector_name = sec.name;
      }
      if (batchCompany) {
        interest.company_id = batchCompany as number;
      }

      await ClientService.bulkAddInterest(selectedClientIds, interest);
      onSuccess(`Interest assigned to ${selectedClientIds.length} client(s)`);
      setAssignDialog(false);
      setSelectedClientIds([]);
      setBatchSector('');
      setBatchCompany('');
    } catch (err) {
      onError('Failed to assign interests: ' + (err as Error).message);
    } finally {
      setProcessing(false);
    }
  };

  // ── Notification Control ────────────────────────────────────────────

  const handleBulkNotifyToggle = async () => {
    if (!notifySector) {
      onError('Select a sector');
      return;
    }

    setProcessing(true);
    try {
      await ClientService.bulkToggleNotify(notifySector, notifyEnabled);
      onSuccess(`Auto-notify ${notifyEnabled ? 'enabled' : 'disabled'} for all "${notifySector}" interests`);
      setNotifyDialog(false);
    } catch (err) {
      onError('Failed to toggle notifications: ' + (err as Error).message);
    } finally {
      setProcessing(false);
    }
  };

  const uniqueSectorNames = Array.from(new Set(sectors.map(s => s.name))).sort();

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Batch Interest Assignment */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <GroupAddIcon />
                Batch Interest Assignment
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Assign a sector or company interest to multiple clients at once.
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Sector</InputLabel>
                <Select
                  value={batchSector}
                  onChange={(e) => setBatchSector(e.target.value as number)}
                  label="Sector"
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {sectors.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Company (optional)</InputLabel>
                <Select
                  value={batchCompany}
                  onChange={(e) => setBatchCompany(e.target.value as number)}
                  label="Company (optional)"
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {companies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name} {c.ticker ? `(${c.ticker})` : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Select Clients</InputLabel>
                <Select
                  multiple
                  value={selectedClientIds}
                  onChange={(e) => setSelectedClientIds(e.target.value as number[])}
                  label="Select Clients"
                  renderValue={(selected) => `${selected.length} client(s) selected`}
                >
                  {clients.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      <Checkbox checked={selectedClientIds.includes(c.id)} size="small" />
                      <ListItemText primary={`${c.first_name} ${c.last_name}`} secondary={c.email} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedClientIds.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  {selectedClientIds.map((id) => {
                    const c = clients.find(cl => cl.id === id);
                    return c ? (
                      <Chip
                        key={id}
                        label={`${c.first_name} ${c.last_name}`}
                        size="small"
                        onDelete={() => setSelectedClientIds(prev => prev.filter(i => i !== id))}
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ) : null;
                  })}
                </Box>
              )}

              <Button
                variant="contained"
                startIcon={<GroupAddIcon />}
                onClick={() => setAssignDialog(true)}
                disabled={selectedClientIds.length === 0 || (!batchSector && !batchCompany)}
                fullWidth
              >
                Assign to {selectedClientIds.length} Client(s)
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Control */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <NotifyIcon />
                Notification Control
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Enable or disable auto-notify for all clients interested in a sector.
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Sector</InputLabel>
                <Select
                  value={notifySector}
                  onChange={(e) => setNotifySector(e.target.value)}
                  label="Sector"
                >
                  <MenuItem value=""><em>Select sector</em></MenuItem>
                  {uniqueSectorNames.map((name) => (
                    <MenuItem key={name} value={name}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={notifyEnabled}
                    onChange={(e) => setNotifyEnabled(e.target.checked)}
                  />
                }
                label={notifyEnabled ? 'Enable auto-notify' : 'Disable auto-notify'}
                sx={{ mb: 2 }}
              />

              <Button
                variant="contained"
                color={notifyEnabled ? 'success' : 'warning'}
                startIcon={<NotifyIcon />}
                onClick={() => setNotifyDialog(true)}
                disabled={!notifySector}
                fullWidth
              >
                {notifyEnabled ? 'Enable' : 'Disable'} for "{notifySector || '...'}"
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirm Batch Assign Dialog */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)}>
        <DialogTitle>Confirm Batch Assignment</DialogTitle>
        <DialogContent>
          <Typography>
            Assign {batchSector ? sectors.find(s => s.id === batchSector)?.name : ''}
            {batchCompany ? ` / ${companies.find(c => c.id === batchCompany)?.name}` : ''} interest
            to <strong>{selectedClientIds.length}</strong> client(s)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(false)} disabled={processing}>Cancel</Button>
          <Button onClick={handleBatchAssign} variant="contained" disabled={processing}>
            {processing ? 'Assigning...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Notify Toggle Dialog */}
      <Dialog open={notifyDialog} onClose={() => setNotifyDialog(false)}>
        <DialogTitle>Confirm Notification Change</DialogTitle>
        <DialogContent>
          <Alert severity={notifyEnabled ? 'info' : 'warning'} sx={{ mb: 1 }}>
            This will {notifyEnabled ? 'enable' : 'disable'} auto-notify for <strong>all</strong> client
            interests in "{notifySector}".
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyDialog(false)} disabled={processing}>Cancel</Button>
          <Button
            onClick={handleBulkNotifyToggle}
            variant="contained"
            color={notifyEnabled ? 'success' : 'warning'}
            disabled={processing}
          >
            {processing ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkActions;
